import type { Session } from '@supabase/supabase-js';
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from '../lib/supabase.ts';
import type { AuthAllapotNev } from './belepes.ts';

export interface Profil { user_id: string; email: string; nev: string; telefon: string | null; super_admin: boolean }
export interface AuthErtek {
  allapot: AuthAllapotNev; session: Session | null; profil: Profil | null;
  kijelentkezes(): Promise<void>; frissites(): Promise<void>;
}
const Ctx = createContext<AuthErtek | null>(null);

async function allapotSzamitas(session: Session | null): Promise<{ allapot: AuthAllapotNev; profil: Profil | null }> {
  if (!session) return { allapot: 'kijelentkezve', profil: null };
  const aal = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (aal.error) throw aal.error;
  const { data: profil, error } = await supabase.from('profiles').select('user_id, email, nev, telefon, super_admin')
    .eq('user_id', session.user.id).maybeSingle();
  if (error) throw error;
  if (aal.data.currentLevel !== 'aal2') {
    return { allapot: aal.data.nextLevel === 'aal2' ? 'mfa_kod' : 'mfa_beallitas', profil };
  }
  return { allapot: 'kesz', profil };
}

type Allapot = { allapot: AuthAllapotNev; session: Session | null; profil: Profil | null; hiba: unknown };
/** Tartalmilag azonos-e a frissítés eredménye az előző állapottal (hogy ne cseréljük le fölöslegesen az objektumot). */
function azonos(e: Allapot, a: { allapot: AuthAllapotNev; profil: Profil | null }, session: Session | null): boolean {
  const p = e.profil, q = a.profil;
  const profilEgyezik = p === q || (!!p && !!q && p.user_id === q.user_id && p.email === q.email && p.nev === q.nev
    && p.telefon === q.telefon && p.super_admin === q.super_admin);
  return !e.hiba && e.allapot === a.allapot && e.session?.user.id === session?.user.id && profilEgyezik;
}

export function AuthSzolgaltato({ children }: { children: ReactNode }) {
  const [ertek, setErtek] = useState<{ allapot: AuthAllapotNev; session: Session | null; profil: Profil | null; hiba: unknown }>(
    { allapot: 'betolt', session: null, profil: null, hiba: null });

  const frissites = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    try {
      const a = await allapotSzamitas(data.session);
      setErtek((e) => (azonos(e, a, data.session) ? e : { ...a, session: data.session, hiba: null }));
    } catch (hiba) {
      setErtek((e) => {
        if (e.allapot !== 'betolt') {
          // Átmeneti hálózati hiba ne dobja hibaoldalra az már betöltött felületet: megtartjuk az előző állapotot.
          console.warn('Az állapot frissítése nem sikerült, az előző állapotot megtartjuk', hiba);
          return e;
        }
        return { ...e, hiba };
      });
    }
  }, []);

  useEffect(() => {
    void frissites();
    const { data } = supabase.auth.onAuthStateChange(() => {
      // A callbackből közvetlenül Supabase-hívás holtpontot okozhat (supabase-js ismert korlát), ezért a következő körben.
      setTimeout(() => void frissites(), 0);
    });
    return () => data.subscription.unsubscribe();
  }, [frissites]);

  const kijelentkezes = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) return;
    // Pl. offline: legalább a helyi munkamenetet töröljük; ha az is hibás, továbbdobjuk.
    const { error: helyiHiba } = await supabase.auth.signOut({ scope: 'local' });
    if (helyiHiba) throw helyiHiba;
  }, []);

  if (ertek.hiba) throw ertek.hiba; // a router errorElement-je mutatja
  return <Ctx.Provider value={{ allapot: ertek.allapot, session: ertek.session, profil: ertek.profil, kijelentkezes, frissites }}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthErtek {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth csak AuthSzolgaltato alatt használható.');
  return v;
}
